// An external forecast model's expected delivery (ProcessedModelSchema.deliveryExpectation): when forecasts should
// be issued and how long after each issue time its file may land and still be on time. Parsing and validation
// only, with no browser imports, so POST /api/model-schemas (erodeapifunction) and the deploy wizard share the
// rules. Grading lives in utils/externalDelivery.ts. See docs/architecture/EXTERNAL_FORECASTS.md §6.

import { isValidCron, minCronGapMinutes } from "./scheduleFrequency";

export interface DeliveryExpectation {
  /** Expected ISSUE times (AWS 6-field or standard 5-field cron), in `timezone`. */
  cronExpression: string;
  timezone: string;
  /** How long after an issue time its file may land and still be on time. */
  allowanceMinutes: number;
  /** ISO time grading starts from; earlier slots are not expected. Reset whenever the cron or timezone changes. */
  since: string;
  /** The wizard's schedule-editor label ("Daily", "CUSTOM"…), so an edit reopens on the same mode; not read for grading. */
  frequency?: string;
}

export const DEFAULT_ALLOWANCE_MINUTES = 60;
export const MAX_ALLOWANCE_MINUTES = 72 * 60;
/** Issue times closer together than this make a calendar of hundreds of cells a day; forecasts aren't that frequent. */
export const MIN_ISSUE_GAP_MINUTES = 60;

const frequencyOf = (v: unknown): string | undefined =>
  typeof v === "string" && /^[A-Za-z ]{1,20}$/.test(v) ? v : undefined;

const isPlainObject = (v: unknown): v is Record<string, unknown> => v !== null && typeof v === "object" && !Array.isArray(v);

/** True for an IANA zone name (or UTC) the runtime knows. Intl throws a RangeError for anything else. */
export function isValidTimeZone(timeZone: unknown): boolean {
  if (typeof timeZone !== "string" || !timeZone.trim()) return false;
  try {
    new Intl.DateTimeFormat("en-US", { timeZone });
    return true;
  } catch {
    return false;
  }
}

/** The stored value (AWSJSON: an object, or a JSON string from an older client) as an expectation; null when unset or unreadable. */
export function parseDeliveryExpectation(raw: unknown): DeliveryExpectation | null {
  let value: unknown = raw;
  if (typeof raw === "string") {
    if (!raw.trim()) return null;
    try {
      value = JSON.parse(raw);
    } catch {
      return null;
    }
  }
  if (!isPlainObject(value)) return null;
  const cronExpression = typeof value.cronExpression === "string" ? value.cronExpression.trim() : "";
  if (!cronExpression) return null;
  const allowance = Number(value.allowanceMinutes);
  return {
    cronExpression,
    timezone: isValidTimeZone(value.timezone) ? String(value.timezone) : "UTC",
    // Capped as the validator caps it, so a hand-edited row can't stretch the calendar's pending window
    allowanceMinutes: Number.isFinite(allowance) && allowance >= 0 ? Math.min(allowance, MAX_ALLOWANCE_MINUTES) : DEFAULT_ALLOWANCE_MINUTES,
    since: typeof value.since === "string" && !Number.isNaN(Date.parse(value.since)) ? value.since : "",
    ...(frequencyOf(value.frequency) ? { frequency: frequencyOf(value.frequency) } : {}),
  };
}

/** Which rule a value broke, so a caller can word it its own way (the wizard names the box, the API the field). */
export type DeliveryExpectationErrorCode = "not_object" | "invalid_cron" | "too_frequent" | "invalid_timezone" | "invalid_allowance";

type NormalizeResult =
  | { value: DeliveryExpectation | null; error?: undefined; code?: undefined }
  | { error: string; code: DeliveryExpectationErrorCode; value?: undefined };

/** A number, or a numeric string that isn't blank; anything else (blank, [], true) is NaN rather than JavaScript's 0. */
const toMinutes = (v: unknown): number => {
  if (typeof v === "number") return v;
  if (typeof v === "string" && v.trim() !== "") return Number(v);
  return Number.NaN;
};

/**
 * Check what a caller sent ({ cronExpression, timezone?, allowanceMinutes? }) and return the value to store, with
 * `since` set: kept from `previous` when the cron and timezone are unchanged (an allowance edit doesn't restart
 * grading), else `now`. `null` / `undefined` / "" input clears the expectation.
 */
export function normalizeDeliveryExpectation(
  raw: unknown,
  { previous = null, now = new Date(), field = "deliveryExpectation" }: { previous?: DeliveryExpectation | null; now?: Date; field?: string } = {}
): NormalizeResult {
  if (raw === null || raw === undefined || raw === "") return { value: null };
  if (!isPlainObject(raw)) {
    return { code: "not_object", error: `${field} must be an object: { cronExpression, timezone, allowanceMinutes }` };
  }

  const cronExpression = typeof raw.cronExpression === "string" ? raw.cronExpression.trim() : "";
  if (!cronExpression || !isValidCron(cronExpression)) {
    return { code: "invalid_cron", error: `${field}.cronExpression is not a valid cron, e.g. "0 0,6,12,18 * * ? *" (the expected issue times)` };
  }
  const timezone = raw.timezone === undefined || raw.timezone === null || raw.timezone === "" ? "UTC" : raw.timezone;
  if (!isValidTimeZone(timezone)) {
    return { code: "invalid_timezone", error: `${field}.timezone "${String(timezone)}" is not a timezone (use an IANA name like America/Vancouver, or UTC)` };
  }
  const gap = minCronGapMinutes(cronExpression, String(timezone));
  if (gap !== null && gap < MIN_ISSUE_GAP_MINUTES) {
    return { code: "too_frequent", error: `${field}.cronExpression expects an issue every ${gap} minutes at its most frequent; at most one an hour is supported` };
  }
  const allowanceMinutes =
    raw.allowanceMinutes === undefined || raw.allowanceMinutes === null ? DEFAULT_ALLOWANCE_MINUTES : toMinutes(raw.allowanceMinutes);
  if (!Number.isFinite(allowanceMinutes) || allowanceMinutes < 0 || allowanceMinutes > MAX_ALLOWANCE_MINUTES) {
    return { code: "invalid_allowance", error: `${field}.allowanceMinutes must be a number of minutes from 0 to ${MAX_ALLOWANCE_MINUTES} (72 hours)` };
  }

  const sameSchedule =
    previous && previous.since && previous.cronExpression === cronExpression && previous.timezone === String(timezone);
  return {
    value: {
      cronExpression,
      timezone: String(timezone),
      allowanceMinutes: Math.round(allowanceMinutes),
      since: sameSchedule ? previous.since : now.toISOString(),
      ...(frequencyOf(raw.frequency) ? { frequency: frequencyOf(raw.frequency) } : {}),
    },
  };
}

/** "within 6 h" / "within 45 min" / "by the issue time": the allowance as the wizard and calendar say it. */
export function describeAllowance(minutes: number): string {
  if (!minutes) return "by the issue time";
  if (minutes % 60 === 0) return `within ${minutes / 60} h`;
  if (minutes > 90) return `within ${(minutes / 60).toFixed(1).replace(/\.0$/, "")} h`;
  return `within ${minutes} min`;
}
